import { useRef, useEffect, useCallback } from 'react';
import { Search, X, FolderTree, Calendar, Clock, RefreshCw } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';

export function DocsToolbar() {
  const {
    viewMode, setViewMode,
    roots, activeRoot, setActiveRoot,
    searchQuery, searchFiles, clearSearch, searching,
    fetchTree, treeLoading,
  } = useDocsStore();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSearchChange = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 2) {
      clearSearch();
      return;
    }

    debounceRef.current = setTimeout(() => {
      searchFiles(value);
    }, 300);
  }, [searchFiles, clearSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="flex items-center gap-3 p-3 border-b border-border-subtle bg-bg-secondary flex-wrap">
      {/* Search */}
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search files..."
          defaultValue={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full bg-bg-tertiary border border-border-subtle text-text-primary rounded-md pl-8 pr-8 py-1.5 text-xs placeholder:text-text-muted outline-none focus:border-accent-purple/50"
        />
        {(searchQuery || searching) && (
          <button
            onClick={() => {
              clearSearch();
              if (searchInputRef.current) searchInputRef.current.value = '';
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Root selector */}
      <select
        value={activeRoot}
        onChange={(e) => setActiveRoot(e.target.value)}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-2.5 py-1.5 text-xs"
      >
        {roots.map(root => (
          <option key={root.id} value={root.id}>{root.label}</option>
        ))}
      </select>

      {/* View toggle */}
      <div className="flex items-center bg-bg-tertiary rounded-md border border-border-subtle">
        <button
          onClick={() => setViewMode('today')}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-l-md transition-colors ${
            viewMode === 'today'
              ? 'bg-accent-purple/20 text-accent-purple-light'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <Clock className="h-3 w-3" />
          Today
        </button>
        <button
          onClick={() => setViewMode('tree')}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${
            viewMode === 'tree'
              ? 'bg-accent-purple/20 text-accent-purple-light'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <FolderTree className="h-3 w-3" />
          Tree
        </button>
        <button
          onClick={() => setViewMode('legacy')}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-r-md transition-colors ${
            viewMode === 'legacy'
              ? 'bg-accent-purple/20 text-accent-purple-light'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <Calendar className="h-3 w-3" />
          Logs
        </button>
      </div>

      {/* Refresh */}
      <button
        onClick={() => fetchTree()}
        disabled={treeLoading}
        className="p-1.5 text-text-muted hover:text-text-primary rounded transition-colors disabled:opacity-50"
        title="Refresh tree"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${treeLoading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}

export default DocsToolbar;
